/**
 * Layer-2 browsing scope for list query persistence.
 * Mirrors how enterprise URLs nest org → workspace → team.
 */
export type BrowsingScopeLevel = 'organization' | 'workspace' | 'team';

export type ListQueryPersistenceRoute = {
  /** Path pattern from routing builders (myRouting / appRouting / cockpitRoutes). */
  pattern: string;
  browsingScope: BrowsingScopeLevel;
  /** Route param that further isolates the list (e.g. resourceTypeId). */
  subtypeParam?: string;
};

export type ListQueryDefinition<T extends object = object> = {
  /** Persistence key segment (and default identity). */
  listId: string;
  schema: { parse: (data: unknown) => T };
  routes: ListQueryPersistenceRoute[];
};

/**
 * Derive LocationService parseQueryParams from a list-query registry.
 * Single source: each entry's `schema` — do not re-list schemas elsewhere.
 */
export function toLocationParseQueryParams<
  const T extends Record<
    string,
    { schema: { parse: (data: unknown) => object } }
  >
>(
  queries: T
): {
  [K in keyof T]: (
    params: Record<string, unknown>
  ) => ReturnType<T[K]['schema']['parse']>;
} {
  return Object.fromEntries(
    Object.entries(queries).map(([key, entry]) => [
      key,
      (params: Record<string, unknown>) => entry.schema.parse(params)
    ])
  ) as {
    [K in keyof T]: (
      params: Record<string, unknown>
    ) => ReturnType<T[K]['schema']['parse']>;
  };
}
